/**
 * SELECT-POPULATIONS.MODAL
 * Select list of populations
 */
import Modal from "@/components/modal"
import { connect } from "react-redux"
import { withTranslation, WithTranslation } from "react-i18next"
import { DashboardFilters, Filter, FilterPopulation } from "@/redux/filter.types"
import { useEffect, useRef, useState } from "react"
import PageLoader from "@/components/page-loader"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"
import { store } from "@/index"
import { filterActivate, filterFetch } from "@/redux/filter.actions"
import Chip from "@/components/chip"
import { Session } from "@/redux/_session.types"
import ModalConfirm from "@/components/modal-confirm"
import GetPopulationUsersModal from "./get-population-users.modal"
import getUserFilterDates, { UserFilterDates } from "@/utils/get-user-filter-dates.utils"
import { TemplateState } from "@/redux/template.types"
import { Population } from "@/redux/population.types"
import { fetchAttributes, fetchFilters } from "@/redux/_archive.actions"
import { surveyEditOne, surveyFetch, surveyGet } from "@/redux/survey.actions"
import Dropdown from "@/components/dropdown"
import { Survey, SurveyState } from "@/redux/survey.types"
import makeInsensitive from "@/utils/make-insensitive.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExchangeAlt, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import { flatten, sortBy } from "lodash"
import Checkbox from "@/components/checkbox"
import { Topic } from "@/redux/topic.types"
import { topicFetch } from "@/redux/topic.actions"

interface StateProps extends WithTranslation {
  _session: Session
  survey: SurveyState
  template: TemplateState
}

interface OwnProps {
  allowNoFilter?: boolean //Allow to write custom value for the population
  excludedFilters?: string[] //Possible to hide filters
  isSurveyFiltersIncluded?: boolean //Search filters from the previous surveys
  isAccountSelectable?: boolean //Possible to select whole account
  isSurveyActiveSelected?: boolean //Get filters for the current survey
  isObserverSurveyRestriction?: boolean //Show survey restriction section (observer only)
  projectSegmentAxisId?: string //Load filters from the template segmentation
  isWithoutDefaultFilters?: boolean //Don't load default filters (gender, birth date)
  onClose?: Function //Trigger on close
  onNext: Function //Trigger on confirm
  selectedPopulations: Population[] //List of selected populations
  restrictedSurveys?: string[] //List of restricted survey IDs (observer only)
  subtitle?: string //Display secondary title
  title?: string //Display custom title
}

type Props = StateProps & OwnProps

const MODAL_ASK_SAVE = "MODAL_ASK_SAVE"
const MODAL_POPULATION_USERS = "MODAL_POPULATION_USERS"

function SelectPopulationsModal(props: Props) {
  const { t } = props
  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)

  //Search
  const [currentSearch, setCurrentSearch] = useState("")

  //Custom value
  const [allowNoFilter, setAllowNoFilter] = useState(
    props.selectedPopulations.length === 1 ? !props.selectedPopulations[0].filterName : false
  )

  //Current population to display
  const [currentPopulationName, setCurrentPopulationName] = useState("")

  //Current modal
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Show hide button for the filters
  const [displayExcludeFilter] = useState(props.excludedFilters ? true : false)

  //List of excluded filters for the active user
  const [excludedFilters, setExcludedFilters] = useState(props.excludedFilters ? props.excludedFilters : [])

  //Component is loading
  const [isLoading, setIsLoading] = useState(true)

  //Save changes
  const [isEdited, setIsEdited] = useState(false)

  //Hide low populations
  const [hideSmallPopulations, setHideSmallPopulations] = useState(true)

  //Selected populations
  const [selectedPopulations, setSelectedPopulations] = useState<Population[]>(props.selectedPopulations)

  // Keep the selection list local: FILTER_GET resets the dashboard filters.
  const [availableFilters, setAvailableFilters] = useState<Filter[]>([])

  //Selected Filters
  const [selectedSurvey, setSelectedSurvey] = useState(getCustomerFilterName())
  const [selectedFilter, setSelectedFilter] = useState(new Filter())

  //Search by filters
  const [searchByFilters, setSearchByFilters] = useState(false)

  //Restricted surveys (observer only)
  const [restrictedSurveys, setRestrictedSurveys] = useState<string[]>(props.restrictedSurveys ?? [])

  //Change filter active
  useEffect(() => {
    loadAttributes()

    async function loadAttributes() {
      if (searchByFilters && selectedFilter.name.length) {
        setIsLoading(true)

        for (let i = 0; i < props.survey.list.length; i++) {
          const survey = props.survey.list[i]
          const attributes: any = await store.dispatch(
            fetchAttributes(new DashboardFilters(), selectedFilter.name, survey.id)
          )

          if (!attributes.error) {
            attributes.forEach((attribute) => {
              attribute.id = selectedFilter.name + "_" + attribute.name
            })

            store.dispatch(surveyEditOne(survey.id, "attributesByFilter", sortBy(attributes, "name")))
          } else {
            store.dispatch(surveyEditOne(survey.id, "attributesByFilter", []))
          }
        }

        setIsLoading(false)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter.name, searchByFilters])

  //Load survey if asked
  useEffect(() => {
    loadSurveys()

    async function loadSurveys() {
      if (props.isSurveyFiltersIncluded) {
        let surveys: any = []
        surveys = await store.dispatch(surveyFetch())
        store.dispatch(surveyGet(surveys.error ? [] : surveys, [], false))
      }
    }
  }, [props.isSurveyFiltersIncluded])

  //Load filters at start
  useEffect(() => {
    load()

    async function load() {
      //Init filters
      let filters: Filter[] = []

      //Init loading
      setIsLoading(true)

      //Case when selected survey is not defined
      //Load filters that are in the database at this time
      if (selectedSurvey.id === "") {
        //Load filters
        const filterResponse: any = await store.dispatch(
          filterFetch(true, props.isWithoutDefaultFilters ? false : true)
        )
        filters = filterResponse.error ? [] : filterResponse.map((x) => new Filter(x))

        //Update values for default filtres
        filters.forEach((filter: Filter) => {
          if (["gender", "birthDate", "companyWelcomeDate"].indexOf(filter.id) > -1) {
            filter = updateDefaultFilter(filter)
          }
        })

        //Load populations from template (if specified)
        if (props.projectSegmentAxisId) {
          const topicsResponse: any = await store.dispatch(topicFetch(props.template.active.id))
          const topics: Topic[] = topicsResponse.error ? [] : topicsResponse.map((x) => new Topic(x))

          //For each topic at start create filter
          topics
            .filter((x) => x.Questions.filter((x) => x.choiceCount === 0))
            .forEach((topic: Topic) => {
              filters.unshift(
                new Filter({
                  id: topic.id,
                  isTemplateFilter: true,
                  name: topic.label,
                  populations: flatten(
                    topic.Questions.filter((x) => x.choiceCount === 0).map((x) => x.Propositions)
                  ).map((x) => new FilterPopulation({ name: x.label }))
                })
              )
            })
        }
      }
      //If survey id is defined load filters from the survey
      else {
        let surveyFilters: any = []
        let surveyAttributes: any = []

        //Load filters for the survey
        surveyFilters = await store.dispatch(fetchFilters(new DashboardFilters(), selectedSurvey.id))
        surveyAttributes = await store.dispatch(fetchAttributes(new DashboardFilters(), null, selectedSurvey.id))

        //Loop on filter
        if (!surveyFilters.error && !surveyAttributes.error) {
          if (hideSmallPopulations) {
            surveyAttributes = surveyAttributes.filter((x) => x.usersCount >= props._session.participationMin)
          }

          surveyFilters.forEach((filter) => {
            filters.push(
              new Filter({
                id: filter.name,
                name: filter.name,
                populations: surveyAttributes
                  .filter((x) => x.filterName === filter.name)
                  .map((x) => new FilterPopulation({ name: x.name, usersCount: x.usersCount }))
              })
            )
          })
        }
      }

      //Update population id
      filters.forEach((filter) => {
        filter.populations.forEach((population) => {
          population.id = filter.default ? filter.id + "_" + population.id : filter.name + "_" + population.name
        })
      })

      setAvailableFilters(filters)

      //End of load
      setIsLoading(false)
    }

    //For default filter update name of filters and population
    function updateDefaultFilter(filter: Filter) {
      filter.name = t(filter.name)
      filter.populations.forEach((i: FilterPopulation) => {
        i.id = i.name
        if (filter.id === "gender") {
          i.name = t("user_gender_" + i.name)
        } else {
          const value: UserFilterDates | undefined = getUserFilterDates(
            filter.id === "birthDate" ? "BIRTH_DATE" : "COMPANY_WELCOME_DATE",
            null
          ).find((x) => x.id === i.name)
          i.name = value ? value.name : ""
        }
      })

      return filter
    }
  }, [
    t,
    selectedSurvey,
    props._session.interfaceType,
    props.projectSegmentAxisId,
    props.isWithoutDefaultFilters,
    props.template.active.id,
    hideSmallPopulations
  ])

  //Close modal
  function closeModal() {
    if (savingRef.current) return
    if (isEdited) {
      setCurrentModal(MODAL_ASK_SAVE)
    } else {
      closeModalConfirm()
    }
  }

  //Confirm close
  function closeModalConfirm() {
    if (props.onClose) {
      props.onClose()
    }
  }

  //Get name for custom filter
  //If no survey selected go for filters_custom importer
  function getCustomerFilterName() {
    return props.isSurveyActiveSelected ? props.survey.active : new Survey({ name: t("filters_custom") })
  }

  //Get populations according search
  function getPopulations(list: FilterPopulation[]) {
    return currentSearch.length ? list.filter((x) => makeInsensitive(x.name).indexOf(currentSearch) > -1) : list
  }

  //Detect if the filter is active for the user
  function isFilterActive(filterName: string) {
    return excludedFilters.indexOf(filterName) === -1
  }

  //Click on chips to add or remove a population from the selected list
  function selectPopulation(filter: Filter, population: FilterPopulation, isTemplatePopulation: boolean) {
    setIsEdited(true)
    if (isPopulationSelected(population.id)) {
      setSelectedPopulations(selectedPopulations.filter((x) => x.id !== population.id))
    } else {
      setSelectedPopulations(
        selectedPopulations.concat([
          {
            filterName: filter.name,
            name: population.name,
            id: population.id,
            isTemplatePopulation
          }
        ])
      )
    }
  }

  //Check if population is selected
  function isPopulationSelected(populationId: string) {
    return selectedPopulations.findIndex((x) => x.id === populationId) > -1
  }

  //Toggle a survey in the restricted list
  function toggleRestrictedSurvey(surveyId: string) {
    setIsEdited(true)
    setRestrictedSurveys((prev) =>
      prev.includes(surveyId) ? prev.filter((id) => id !== surveyId) : prev.concat(surveyId)
    )
  }

  //Send selected populations
  async function next() {
    if (savingRef.current || isLoading) return
    savingRef.current = true
    setIsSaving(true)
    try {
      const saved = await props.onNext(
        allowNoFilter
          ? selectedPopulations.map((obj) => ({
              ...obj,
              filterName: null
            }))
          : selectedPopulations,
        allowNoFilter ? excludedFilters : [],
        props.isObserverSurveyRestriction ? restrictedSurveys : []
      )
      if (saved !== false) closeModalConfirm()
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  //Search into list of populations
  function search(value) {
    setCurrentSearch(makeInsensitive(value))
  }

  //Open user modal
  function showUsers(filter: Filter, populationName: string) {
    store.dispatch(filterActivate(filter))
    setCurrentPopulationName(populationName)
    setCurrentModal(MODAL_POPULATION_USERS)
  }

  return (
    <Modal
      isCloseButtonVisible={props.onClose ? true : false}
      isLarge
      onSearch={search}
      onClose={closeModal}
      onNext={next}
      nextDisabled={isLoading || isSaving}
      status={isSaving ? STATUS_SAVING : undefined}
      title={props.title ? props.title : t("populations_select")}>
      {currentModal === MODAL_ASK_SAVE && (
        <ModalConfirm onNo={() => closeModalConfirm()} onYes={next} textBold={t("utils_ask_confirm_edits")} />
      )}

      {currentModal === MODAL_POPULATION_USERS && (
        <GetPopulationUsersModal onClose={() => setCurrentModal(null)} populationName={currentPopulationName} />
      )}

      <div className="flex flex-wrap items-start gap-x-8 gap-y-4 mb-4">
        <div className="flex-1 min-w-0" style={{ flexBasis: 320 }}>
          {props.subtitle && (
            <p className="grey-t" style={{ marginTop: 0, marginBottom: 12 }}>
              <b>{props.subtitle}</b>
            </p>
          )}

          {props.isSurveyActiveSelected && (
            <Checkbox
              text={t("select_population_hide_small")}
              onClick={() => setHideSmallPopulations(!hideSmallPopulations)}
              active={!hideSmallPopulations}
            />
          )}

          {props.allowNoFilter && (
            <Checkbox
              active={allowNoFilter}
              text={t("select_population_no_filter")}
              onClick={() => {
                setAllowNoFilter(!allowNoFilter)
                setSelectedPopulations([])
              }}
            />
          )}

          {props.isAccountSelectable && (
            <div>
              <h3>{t("account_placeholder")}</h3>
              <div className="flex">
                <Chip
                  onClick={() => setSelectedPopulations([])}
                  isWithMargin
                  color={!selectedPopulations.length ? props._session.accountColors.active : ""}>
                  {props._session.accountName}
                </Chip>
              </div>
            </div>
          )}
        </div>

        {props.isSurveyFiltersIncluded && (
          <div className="shrink-0 ml-auto">
            <div className="grey-t">{t("filters")}</div>
            <div className="flex items-center gap-2">
              {searchByFilters ? (
                <Dropdown
                  displayField="name"
                  active={selectedFilter.id}
                  value={selectedFilter.name}
                  list={availableFilters}
                  onSelect={(item) => setSelectedFilter(item)}
                />
              ) : (
                <Dropdown
                  displayField="name"
                  active={selectedSurvey.id}
                  value={selectedSurvey.name}
                  list={[getCustomerFilterName()].concat(props.survey.list)}
                  onSelect={(item) => setSelectedSurvey(item)}
                />
              )}
              <ListButton
                onClick={() => setSearchByFilters(!searchByFilters)}
                tooltipPosition="left"
                icon={faExchangeAlt}
                text={t("filter_switch_search")}
              />
            </div>
          </div>
        )}
      </div>

      <div>
        <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
          {searchByFilters ? (
            //Displey all filters by survey
            <div>
              {props.survey.list
                .filter((x) => x.attributesByFilter.length)
                .map((survey) => (
                  <div key={survey.id}>
                    <h3>{survey.name}</h3>

                    <div className="flex flex-wrap">
                      {survey.attributesByFilter.map((attribute) => (
                        <Chip
                          key={attribute.id}
                          count={attribute.usersCount}
                          countWarning={attribute.usersCount < props._session.participationMin}
                          isWithMargin
                          onClick={() => selectPopulation(selectedFilter, attribute, false)}
                          color={isPopulationSelected(attribute.id) ? props._session.accountColors.active : ""}>
                          {attribute.name}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            //Display by filters
            <div>
              {availableFilters.map(
                (filter) =>
                  getPopulations(filter.populations).length > 0 && (
                    <div key={filter.id}>
                      {!allowNoFilter && (
                        <div className="flex">
                          <h3>
                            <span className={isFilterActive(filter.name) ? "" : "medgrey-t"}>{filter.name}</span>

                            {filter.isTemplateFilter && (
                              <span className="grey-t">&nbsp;- {t("question_type_choice_0")}</span>
                            )}

                            {displayExcludeFilter && (
                              <span className="grey-t">
                                &nbsp;
                                <FontAwesomeIcon
                                  className="_hover"
                                  style={{
                                    fontSize: 18,
                                    margin: "-2px 4px"
                                  }}
                                  icon={isFilterActive(filter.name) ? faEye : faEyeSlash}
                                  onClick={() =>
                                    setExcludedFilters(
                                      isFilterActive(filter.name)
                                        ? excludedFilters.concat(filter.name)
                                        : excludedFilters.filter((x) => x !== filter.name)
                                    )
                                  }
                                />
                              </span>
                            )}

                            {!isFilterActive(filter.name) && (
                              <span className="grey-t" style={{ fontSize: 12 }}>
                                &nbsp;
                                {t("user_excluded_filter", {
                                  filter: filter.name
                                })}
                              </span>
                            )}
                          </h3>
                        </div>
                      )}

                      <div className="flex flex-wrap">
                        {getPopulations(filter.populations).map((population, i) => (
                          <Chip
                            key={i}
                            onInfo={
                              filter.isTemplateFilter || selectedSurvey.id !== ""
                                ? undefined
                                : () => showUsers(filter, population.name)
                            }
                            count={filter.isTemplateFilter ? undefined : population.usersCount}
                            countWarning={population.usersCount < props._session.participationMin}
                            isWithMargin
                            onClick={() => selectPopulation(filter, population, filter.isTemplateFilter ? true : false)}
                            color={isPopulationSelected(population.id) ? props._session.accountColors.active : ""}>
                            {population.name}
                          </Chip>
                        ))}
                      </div>

                      <div className="height-20" />
                    </div>
                  )
              )}
            </div>
          )}
        </PageLoader>

        {props.isObserverSurveyRestriction && props.survey.list.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3>{t("observer_restricted_surveys_title")}</h3>
            <p className="grey-t" style={{ fontSize: 13, marginBottom: 8 }}>
              {t("observer_restricted_surveys_help")}
            </p>
            {props.survey.list.map((survey) => (
              <div key={survey.id} style={{ marginBottom: 6 }}>
                <Checkbox
                  active={restrictedSurveys.includes(survey.id)}
                  text={survey.name}
                  onClick={() => toggleRestrictedSurvey(survey.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  survey: state.survey,
  template: state.template
})

export default connect(mapStateToProps)(withTranslation()(SelectPopulationsModal))
